import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { MapContainer as Map, useMapEvents } from "react-leaflet";
import hasDuplicateCoordinatesOnly from "./hasDuplicateCoordinatesOnly";
import MapLibreGLLayer from "./MapLibreGLLayer";

// We no longer can use event handlers props directly on the MapContainer component,
// only on it's descendant https://react-leaflet.js.org/docs/api-map/#usemapevents
const MapEventsSubscriber = ({ eventHandlers }) => {
  useMapEvents({
    ...eventHandlers,
  });
  return null;
};

MapEventsSubscriber.propTypes = {
  eventHandlers: PropTypes.object,
};

// Prevents infinite dragging across the globe,
// cuts the Pacific Ocean in half
const MAX_BOUNDS = [
  [85, 180],
  [-85, -180],
];

const propTypes = {
  markers: PropTypes.array,
  onMapMove: PropTypes.func,
  children: PropTypes.node,
  bounds: PropTypes.array,
  mapTilerKey: PropTypes.string,
  setMapRef: PropTypes.func,
  isSmallScreen: PropTypes.bool,
  mapMovingBecauseOfSearch: PropTypes.bool,
  eventHandlers: PropTypes.object,
  isMapView: PropTypes.bool,
};

const MapContainer = ({
  children,
  markers,
  bounds,
  mapTilerKey,
  isSmallScreen,
  eventHandlers,
  onMapMove,
  mapMovingBecauseOfSearch,
  isMapView,
  ...props
}) => {
  const [map, setMap] = useState();

  useEffect(() => {
    /**
     * LeafletMap props are immutable, we have to move the map through fitBounds or other methods
     * https://react-leaflet.js.org/docs/api-map/
     */
    if (map && bounds && !isSmallScreen && mapMovingBecauseOfSearch) {
      map.fitBounds(bounds);
    }
  }, [map, bounds, isSmallScreen, mapMovingBecauseOfSearch]);

  const handleMapMove = () => {
    if (onMapMove && map) {
      const size = map.getSize();
      const topLeft = map.containerPointToLatLng([70, 100]);
      const bottomRight = map.containerPointToLatLng([
        size.x - 50,
        size.y - 50,
      ]);
      onMapMove([topLeft, bottomRight]);
    }
  };

  const getBounds = (bounds) => {
    if (hasDuplicateCoordinatesOnly(bounds)) {
      return [
        [bounds[0][0] - 0.01, bounds[0][1] - 0.01],
        [bounds[0][0] + 0.01, bounds[0][1] + 0.01],
      ];
    }

    return bounds;
  };

  /** invalidateSize will check if the container size changed and update the map size
   * if it has. We call this when we switch to and from the map view, because that
   * changes the size of the map container.
   */
  const wasMapView = useRef(isMapView);
  useEffect(() => {
    if (map && isMapView !== wasMapView.current) {
      map.invalidateSize();
    }
    wasMapView.current = isMapView;
  }, [isMapView, map]);

  return (
    <Map
      {...props}
      ref={(map) => {
        setMap(map);
        props.setMapRef?.(map);
      }}
      maxBounds={MAX_BOUNDS}
      bounds={bounds && getBounds(bounds)}
      tap={false} // https://github.com/Leaflet/Leaflet/issues/7255
    >
      <MapLibreGLLayer mapTilerKey={mapTilerKey} />
      <MapEventsSubscriber
        eventHandlers={{
          dragend: handleMapMove,
          zoomend: handleMapMove,
          ...eventHandlers,
        }}
      />
      {markers}
      {children}
    </Map>
  );
};

MapContainer.propTypes = propTypes;

export default MapContainer;
